import './index.css';
import props from './props';
import Renderer from '../renderer';
import { isFunction } from '../helper';
import { TableToolbar } from '../../../TableToolbar';
import { ref, unref, toRefs, computed, defineComponent, type CSSProperties } from 'vue';
import { BasicTableProps, TableColumnScope } from '../../types';
import {
  ElTable,
  ElTableColumn,
  ElPagination,
  ElDropdown,
  ElDropdownMenu,
  ElDropdownItem,
} from 'element-plus';
import { useI18n } from 'vue-i18n';
import dropImg from '../../../../assets/images/dropdown.png';

const TableRef = ref();

function thousandBitNumber(num: number | string | undefined) {
  if (num) {
    return num.toString().replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,');
  } else {
    return Number(num) === 0 ? '0' : '';
  }
}

export default defineComponent({
  name: 'BasicTable',
  props,
  emits: ['size-change', 'current-change', 'search', 'input', 'dropdown-change'],
  setup(props, { slots, attrs, emit }) {
    const { t } = useI18n();
    const {
      filterOptions,
      columns,
      align,
      headerAlign,
      showOverflowTooltip,
      pagination,
      transformI18n,
      dropdownImg,
    } = toRefs(props) as unknown as BasicTableProps & {
      transformI18n: { value: (key: string) => string };
      dropdownImg: { value: string };
    };
    const tableColums = ref([]);

    // 获取 i18n 转换函数
    const i18nTransform = (key: string) => {
      if (unref(transformI18n)) {
        return unref(transformI18n)(key);
      }
      return key;
    };

    // 列表标题语言转换
    unref(columns).forEach((item: any) => (item.label = i18nTransform(item.label)));
    unref(filterOptions).filterColumns?.value?.forEach(
      (item: any) => (item.label = i18nTransform(item.label)),
    );
    const arr = unref(filterOptions).filterColumns
      ? unref(columns).filter((item: any) => {
          return unref(filterOptions).filterColumns?.value?.find(
            (v: any) => v.require && v.label === item.label,
          );
        })
      : unref(columns);
    tableColums.value = arr as any;

    const handleDropdownChange = (key: string, val: any) => {
      emit('dropdown-change', key, val);
    };

    const handleColumnFilterChange = (val: string[]) => {
      const arr = unref(columns).filter((item: any) => {
        return val.includes(item.label);
      });
      tableColums.value = arr as any;
    };

    const handleSearch = (val: string) => {
      emit('search', val);
    };
    const handleInput = (val: string) => {
      emit('input', val);
    };

    const handleSizeChange = (val: number) => {
      unref(pagination).pageSize = val;
      emit('size-change', val);
    };

    const handleCurrentChange = (val: number) => {
      unref(pagination).currentPage = val;
      emit('current-change', val);
    };

    const getStyle = computed((): CSSProperties => {
      return Object.assign(
        {
          width: '100%',
          margin: '16px 0',
          display: 'flex',
          justifyContent:
            unref(pagination).align === 'right'
              ? 'flex-end'
              : unref(pagination).align === 'center'
                ? 'center'
                : 'flex-start',
        },
        unref(pagination).style ?? {},
      );
    });

    const conditions =
      unref(pagination) && unref(pagination).currentPage && unref(pagination).pageSize;
    return () => (
      <>
        <TableToolbar
          filterOptions={filterOptions}
          onColumnFilterChange={(val: string[]) => handleColumnFilterChange(val)}
          onSearch={(val: string) => handleSearch(val)}
          onInput={(val: string) => handleInput(val)}
          keyword={props.keyword}
        >
          {{
            btns: slots?.actions && slots.actions(),
            queryFilter: slots?.queryFilter && slots.queryFilter(),
          }}
        </TableToolbar>
        <ElTable {...props} {...attrs} ref={TableRef}>
          {{
            default: () => {
              return unref(tableColums).map((column: any, index: number) => {
                const defaultSlots = {
                  default: (scope: TableColumnScope) => {
                    if (column?.cellRenderer) {
                      return (
                        <Renderer
                          render={column.cellRenderer}
                          params={Object.assign(scope, {
                            index: scope.$index,
                            props,
                            attrs,
                          })}
                        ></Renderer>
                      );
                    } else if (column?.slot) {
                      return slots?.[column.slot]?.(
                        Object.assign(scope, {
                          index: scope.$index,
                          props,
                          attrs,
                        }),
                      );
                    }
                  },
                };
                const scopedSlots = column?.selections
                  ? {
                      header: (
                        <ElDropdown trigger="click">
                          {{
                            default: (
                              <span class="text-dropdown">
                                {column.label}
                                <img class="icon-dropdown" src={dropImg} alt="" />
                              </span>
                            ),
                            dropdown: (
                              <ElDropdownMenu>
                                {{
                                  default: column?.selections.map((item: any, index: number) => {
                                    return (
                                      <ElDropdownItem
                                        onClick={() =>
                                          handleDropdownChange(column.prop, item.value)
                                        }
                                      >
                                        {i18nTransform(item.label)}
                                      </ElDropdownItem>
                                    );
                                  }),
                                }}
                              </ElDropdownMenu>
                            ),
                          }}
                        </ElDropdown>
                      ),
                      ...defaultSlots,
                    }
                  : column?.headerRenderer
                    ? {
                        header: (scope: TableColumnScope) => {
                          return (
                            <Renderer
                              render={column.headerRenderer}
                              params={Object.assign(scope, {
                                index: scope.$index,
                                props,
                                attrs,
                              })}
                            ></Renderer>
                          );
                        },
                        ...defaultSlots,
                      }
                    : column?.headerSlot
                      ? {
                          header: (scope: TableColumnScope) => {
                            return slots?.[column.headerSlot]?.(
                              Object.assign(scope, {
                                index: scope.$index,
                                props,
                                attrs,
                              }),
                            );
                          },
                          ...defaultSlots,
                        }
                      : defaultSlots;
                if (isFunction(column?.hide) && column?.hide(attrs)) {
                  return column?.hide(attrs);
                }
                return (
                  <ElTableColumn
                    {...column}
                    key={index}
                    align={column.align ? column.align : unref(align)}
                    headerAlign={column.headerAlign ? column.headerAlign : unref(headerAlign)}
                    showOverflowTooltip={
                      Object.keys(column).includes('showOverflowTooltip')
                        ? column.showOverflowTooltip
                        : unref(showOverflowTooltip)
                    }
                  >
                    {scopedSlots}
                  </ElTableColumn>
                );
              });
            },
            append: () => {
              return slots.append && slots.append();
            },
            empty: () => {
              return slots.empty && slots.empty();
            },
            row: () => {
              return slots.row && slots.row();
            },
          }}
        </ElTable>
        {unref(pagination) && (
          <div class="table-pg-contain">
            <div class="table-total">
              {t('共')} {thousandBitNumber(unref(pagination).total)} {t('条记录')}
            </div>
            <div class="table-pg common-table-pg">
              {conditions ? (
                <ElPagination
                  {...attrs}
                  class="pagination"
                  style={unref(getStyle)}
                  {...unref(pagination)}
                  small={
                    props?.paginationSmall
                      ? props?.paginationSmall
                      : unref(pagination).small
                        ? unref(pagination).small
                        : false
                  }
                  layout={unref(pagination).layout ?? 'sizes, prev, pager, next, jumper'}
                  pageSizes={unref(pagination).pageSizes ?? [5, 10, 15, 20]}
                  onSizeChange={(val: number) => handleSizeChange(val)}
                  onCurrentChange={(val: number) => handleCurrentChange(val)}
                  title=""
                ></ElPagination>
              ) : null}
            </div>
          </div>
        )}
      </>
    );
  },
  methods: {
    /** Get Table Methods */
    getTableRef() {
      return TableRef.value;
    },
  },
});
